import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Component, ElementRef, forwardRef, Input, OnDestroy, ViewChild } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete";
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "fh-ai-tag-select",
  templateUrl: "./ai-tag-select.component.html",
  styleUrls: ["./ai-tag-select.component.scss"],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AiTagSelectComponent),
      multi: true,
    },
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
})
export class AiTagSelectComponent implements ControlValueAccessor, OnDestroy {
  @Input() vocabulary: string[] = [];
  @Input() label = "AI Tags";
  @ViewChild("tagInput") tagInput!: ElementRef<HTMLInputElement>;

  selected: string[] = [];
  filtered: string[] = [];
  searchControl = new FormControl("");
  readonly separatorKeys = [ENTER, COMMA];

  private destroy$ = new Subject<void>();

  onChange: (v: string[]) => void = () => {};
  onTouched: () => void = () => {};

  constructor() {
    this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((q) => {
      const query = (q ?? "").toLowerCase().trim();
      this.filtered = this.vocabulary.filter(
        (t) => !this.selected.includes(t) && t.toLowerCase().includes(query)
      );
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string[]): void {
    this.selected = Array.isArray(value) ? [...value] : [];
    this.refreshFiltered();
  }

  registerOnChange(fn: (v: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onFocus(): void {
    this.refreshFiltered();
  }

  onTagSelected(event: MatAutocompleteSelectedEvent): void {
    const tag = event.option.value as string;
    if (tag && !this.selected.includes(tag)) {
      this.selected = [...this.selected, tag];
      this.emit();
    }
    this.searchControl.setValue("", { emitEvent: false });
    if (this.tagInput?.nativeElement) {
      this.tagInput.nativeElement.value = "";
    }
    this.refreshFiltered();
  }

  addOption(event: MatChipInputEvent): void {
    event.chipInput?.clear();
    this.searchControl.setValue("", { emitEvent: false });
  }

  remove(tag: string): void {
    this.selected = this.selected.filter((t) => t !== tag);
    this.emit();
    this.refreshFiltered();
  }

  private emit(): void {
    this.onChange(this.selected);
    this.onTouched();
  }

  private refreshFiltered(): void {
    const query = (this.searchControl.value ?? "").toLowerCase().trim();
    this.filtered = this.vocabulary.filter(
      (t) => !this.selected.includes(t) && t.toLowerCase().includes(query)
    );
  }
}
